<script lang="ts">
    import { createEventDispatcher } from 'svelte';
    import PlayButton, {
        generateIconOverride,
    } from '@amp/web-app-components/src/components/buttons/PlayButton/PlayButton.svelte';
    import { PLAY_STATES } from '@amp/web-app-components/src/constants';
    import PlayingBarsHover from '@amp/web-app-components/src/components/PlayingBars/PlayingBarsHover.svelte';
    import type { IconState } from '@amp/web-app-components/src/components/buttons/PlayButton/constants';

    const dispatch = createEventDispatcher();

    export let translateFn: (
        str: string,
        values?: Record<string, string | number>,
    ) => string;
    export let item;
    export let ariaLabel: string = null;
    export let isExplicit: boolean = false;
    export let isLoading: boolean = false;
    export let isPlaying: boolean = false;
    export let isPaused: boolean = false;
    export let usePlayingBarsHover: boolean = false;

    const iconSetOverride = usePlayingBarsHover
        ? generateIconOverride('non-platter', {
              [PLAY_STATES.PLAYING]: PlayingBarsHover,
              [PLAY_STATES.PAUSE]: PlayingBarsHover,
          })
        : null;

    $: iconState = ((): IconState => {
        if (isLoading) {
            return PLAY_STATES.BUFFER;
        }

        if (isPlaying) {
            return PLAY_STATES.PLAYING;
        }

        if (isPaused) {
            return PLAY_STATES.PAUSE;
        }

        return PLAY_STATES.PLAY;
    })();

    $: buttonAriaLabel = (() => {
        if (ariaLabel) {
            return ariaLabel;
        }

        let label = isPlaying
            ? translateFn('FUSE.Pause')
            : translateFn('FUSE.Play');

        if (isExplicit) {
            return translateFn('AMP.Shared.ContentA.Comma.ContentB', {
                contentA: translateFn('FUSE.Explicit'),
                contentB: label,
            });
        } else {
            return label;
        }
    })();

    function handleClick() {
        if (isPlaying && item.pauseAction) {
            dispatch('pause');
        } else if (isPaused && item.resumeAction) {
            dispatch('resume');
        } else if (!isLoading && item.playAction) {
            dispatch('play');
        }
    }
</script>

<!--
Avoid leaking double clicks, because when this component is a child of TrackListItem, that component has a
double click handler that initiates a fresh play reseting the queue. However, that competes with the play
issued by this component, and leads to a bad PAF state in MK
-->
<!-- svelte-ignore a11y-no-static-element-interactions -->
<div class="interactive-play-button" on:dblclick|stopPropagation>
    <PlayButton
        {iconSetOverride}
        {iconState}
        ariaLabel={buttonAriaLabel}
        iconSet="non-platter"
        on:click={handleClick}
    />
</div>

<style lang="scss">
    @use '@amp/web-shared-styles/app/core/globalvars' as *;

    .interactive-play-button {
        --nonPlatterIconFill: var(
            --nonPlatterOverrideIconColor,
            var(--keyColor)
        );
        height: var(
            --interactivePlayButtonSize,
            #{$global-tracklist-play-button-size}
        );
        width: var(
            --interactivePlayButtonSize,
            #{$global-tracklist-play-button-size}
        );

        :global(.icon) {
            vertical-align: bottom;
        }
    }
</style>
